import Button from "@app/components/button";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import ChevronDownArrow from "@app/components/select/chevron-down";
import { CustomSelect } from "@app/components/select/custom-select";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  FileType,
  MAX_SIZE_UPLOAD,
  Role,
  RoutePath,
  SystemMessage,
} from "@app/constants";
import BookService from "@app/services/http/book.service";
import VoucherService from "@app/services/http/voucher.service";
import { selectAuth } from "@app/store/auth";
import {
  CreateVoucher,
  CreateVoucherBook,
  CreateVoucherKas,
  LabelValue,
} from "@app/types";
import { Images } from "@assets/images";
import { RequestParams } from "@core/services/http/http.type";
import { Form, Formik, FormikContextType } from "formik";
import { ChangeEvent, createRef, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

export const createVoucherValidationSchema = Yup.object().shape({
  expiredTime: Yup.number()
    .required(SystemMessage.INPUT_REQUIRED)
    .integer(SystemMessage.INPUT_INVALID_EXPIRED_TIME)
    .typeError(SystemMessage.INPUT_INVALID_EXPIRED_TIME)
    .positive(SystemMessage.INPUT_INVALID_EXPIRED_TIME),
});

function VoucherImport() {
  const inputFileRefVip = useRef<HTMLInputElement>(null);
  const inputFileRefBook = useRef<HTMLInputElement>(null);
  const inputFileRefVoucherKas = useRef<HTMLInputElement>(null);
  const formRefVip =
    createRef<FormikContextType<{ expiredTime: string; filename?: string }>>();
  const formRefBook =
    createRef<FormikContextType<{ bookId: number; filename?: string }>>();
  const formRefVoucherKas =
    createRef<FormikContextType<{ filename?: string }>>();

  const navigate = useNavigate();

  const userInfo = useSelector(selectAuth);
  const [requestParams, setRequestParams] = useState<RequestParams>({
    ...DEFAULT_REQUEST_PARAMS,
  });
  const [books, setBooks] = useState<LabelValue[]>([]);

  useEffect(() => {
    if (userInfo.role && userInfo.role !== Role.ROOT) {
      navigate(`/${RoutePath.BOOK_MANAGEMENT}`);
    }
  }, [userInfo]);

  const onSubmitVip = (values: { expiredTime: string }) => {
    if (!formRefVip.current) return;

    if (!inputFileRefVip.current?.files?.length) {
      formRefVip.current.setFieldError(
        "filename",
        SystemMessage.INPUT_REQUIRED
      );

      return;
    }

    const file = inputFileRefVip.current.files[0];

    if (
      ![FileType.XLSX, FileType.CSV, FileType.TXT].includes(
        file.type as FileType
      )
    ) {
      formRefVip.current.setFieldError(
        "filename",
        SystemMessage.FILE_TYPE_NOT_ALLOW
      );

      return;
    }

    if (file.size > MAX_SIZE_UPLOAD) {
      formRefVip.current.setFieldError(
        "filename",
        SystemMessage.MAX_SIZE_UPLOAD
      );

      return;
    }

    const createVoucher: CreateVoucher = {
      expiredTime: Number(values.expiredTime),
      file,
    };

    VoucherService.create(createVoucher).then(() => {
      addToast({ text: SystemMessage.IMPORT_SUCCESSFULLY });

      if (formRefVip.current) {
        formRefVip.current.resetForm();
      }

      if (inputFileRefVip.current) {
        inputFileRefVip.current.value = "";
      }
    });
  };

  useEffect(() => {
    BookService.getRetailBooks().then((res) => {
      const bookOptions = res.data.items.map((book: LabelValue) => ({
        label: book.label,
        value: book.value.toString(),
      }));

      setBooks(bookOptions);

      if (
        requestParams.pageNum &&
        res.data.meta.totalPage > 0 &&
        res.data.meta.totalPage < requestParams.pageNum
      ) {
        const newRequestParams: RequestParams = {
          ...requestParams,
          pageNum: DEFAULT_PAGE,
        };

        setRequestParams(newRequestParams);
      }
    });
  }, [requestParams]);

  const onSubmitBook = (values: { bookId: number }) => {
    if (!formRefBook.current) return;

    if (!inputFileRefBook.current?.files?.length) {
      formRefBook.current.setFieldError(
        "filename",
        SystemMessage.INPUT_REQUIRED
      );

      return;
    }

    const file = inputFileRefBook.current.files[0];

    if (
      ![FileType.XLSX, FileType.CSV, FileType.TXT].includes(
        file.type as FileType
      )
    ) {
      formRefBook.current.setFieldError(
        "filename",
        SystemMessage.FILE_TYPE_NOT_ALLOW
      );

      return;
    }

    if (file.size > MAX_SIZE_UPLOAD) {
      formRefBook.current.setFieldError(
        "filename",
        SystemMessage.MAX_SIZE_UPLOAD
      );

      return;
    }

    const createVoucherBook: CreateVoucherBook = {
      bookId: Number(values.bookId),
      file,
    };

    VoucherService.createVoucherBook(createVoucherBook).then(() => {
      addToast({ text: SystemMessage.IMPORT_SUCCESSFULLY });

      if (formRefBook.current) {
        formRefBook.current.resetForm();
      }

      if (inputFileRefBook.current) {
        inputFileRefBook.current.value = "";
      }
    });
  };

  const onAddFileVipClick = () => {
    if (inputFileRefVip.current) {
      inputFileRefVip.current.click();
    }
  };

  const onAddFileBookClick = () => {
    if (inputFileRefBook.current) {
      inputFileRefBook.current.click();
    }
  };

  const handleFileChangeVip = (event: ChangeEvent<HTMLInputElement>) => {
    if (!formRefVip.current) return;

    if (!event.target.files?.length) {
      formRefVip.current.setFieldError(
        "filename",
        SystemMessage.INPUT_REQUIRED
      );
      formRefVip.current.setFieldValue("filename", "");
      formRefVip.current.submitForm();

      return;
    }

    const file = event.target.files[0];

    if (
      ![FileType.XLSX, FileType.CSV, FileType.TXT].includes(
        file.type as FileType
      )
    ) {
      formRefVip.current.setFieldError(
        "filename",
        SystemMessage.INPUT_REQUIRED
      );
      formRefVip.current.setFieldValue("filename", "");
      formRefVip.current.submitForm();

      return;
    }

    if (file.size > MAX_SIZE_UPLOAD) {
      formRefVip.current.setFieldError(
        "filename",
        SystemMessage.MAX_SIZE_UPLOAD
      );
      formRefVip.current.setFieldValue("filename", undefined);
      formRefVip.current.submitForm();

      return;
    }

    formRefVip.current.setFieldError("filename", undefined);
    formRefVip.current.setFieldValue("filename", file.name);
  };

  const handleFileChangeBook = (event: ChangeEvent<HTMLInputElement>) => {
    if (!formRefBook.current) return;

    if (!event.target.files?.length) {
      formRefBook.current.setFieldError(
        "filename",
        SystemMessage.INPUT_REQUIRED
      );
      formRefBook.current.setFieldValue("filename", "");
      formRefBook.current.submitForm();

      return;
    }

    const file = event.target.files[0];

    if (
      ![FileType.XLSX, FileType.CSV, FileType.TXT].includes(
        file.type as FileType
      )
    ) {
      formRefBook.current.setFieldError(
        "filename",
        SystemMessage.INPUT_REQUIRED
      );
      formRefBook.current.setFieldValue("filename", "");
      formRefBook.current.submitForm();

      return;
    }

    if (file.size > MAX_SIZE_UPLOAD) {
      formRefBook.current.setFieldError(
        "filename",
        SystemMessage.MAX_SIZE_UPLOAD
      );
      formRefBook.current.setFieldValue("filename", undefined);
      formRefBook.current.submitForm();

      return;
    }

    formRefBook.current.setFieldError("filename", undefined);
    formRefBook.current.setFieldValue("filename", file.name);
  };

  const onAddFileVoucherKasClick = () => {
    if (inputFileRefVoucherKas.current) {
      inputFileRefVoucherKas.current.click();
    }
  };

  const handleFileVoucherKas = (event: ChangeEvent<HTMLInputElement>) => {
    if (!formRefVoucherKas.current) {
      return;
    }

    if (!event.target.files?.length) {
      formRefVoucherKas.current.setFieldError(
        "filename",
        SystemMessage.INPUT_REQUIRED
      );
      formRefVoucherKas.current.setFieldValue("filename", "");
      formRefVoucherKas.current.submitForm();

      return;
    }

    const file: File = event.target.files[0];

    if (
      ![FileType.XLSX, FileType.CSV, FileType.TXT].includes(
        file.type as FileType
      )
    ) {
      formRefVoucherKas.current.setFieldError(
        "filename",
        SystemMessage.INPUT_REQUIRED
      );
      formRefVoucherKas.current.setFieldValue("filename", "");
      formRefVoucherKas.current.submitForm();

      return;
    }

    if (file.size > MAX_SIZE_UPLOAD) {
      formRefVoucherKas.current.setFieldError(
        "filename",
        SystemMessage.MAX_SIZE_UPLOAD
      );
      formRefVoucherKas.current.setFieldValue("filename", undefined);
      formRefVoucherKas.current.submitForm();

      return;
    }

    formRefVoucherKas.current.setFieldError("filename", undefined);
    formRefVoucherKas.current.setFieldValue("filename", file.name);
  };

  const onSubmitVoucherKas = () => {
    if (!formRefVoucherKas.current) {
      return;
    }

    if (!inputFileRefVoucherKas.current?.files?.length) {
      formRefVoucherKas.current.setFieldError(
        "filename",
        SystemMessage.INPUT_REQUIRED
      );

      return;
    }

    const file: File = inputFileRefVoucherKas.current.files[0];

    if (
      ![FileType.XLSX, FileType.CSV, FileType.TXT].includes(
        file.type as FileType
      )
    ) {
      formRefVoucherKas.current.setFieldError(
        "filename",
        SystemMessage.FILE_TYPE_NOT_ALLOW
      );

      return;
    }

    if (file.size > MAX_SIZE_UPLOAD) {
      formRefVoucherKas.current.setFieldError(
        "filename",
        SystemMessage.MAX_SIZE_UPLOAD
      );

      return;
    }

    const createVoucherKas: CreateVoucherKas = {
      file,
    };

    VoucherService.createVoucherKas(createVoucherKas).then(() => {
      addToast({ text: SystemMessage.IMPORT_SUCCESSFULLY });

      if (formRefVoucherKas.current) {
        formRefVoucherKas.current.resetForm();
      }

      if (inputFileRefVoucherKas.current) {
        inputFileRefVoucherKas.current.value = "";
      }
    });
  };

  return (
    <div className="flex py-6 px-12">
      <div className="bg-white rounded-lg p-4 w-96">
        <Formik
          initialValues={{ expiredTime: "", filename: "" }}
          onSubmit={onSubmitVip}
          innerRef={formRefVip}
          validationSchema={createVoucherValidationSchema}
          validateOnChange
          validateOnBlur
        >
          <Form>
            <div className="font-bold">Import mã VIP</div>
            <FormControl name="expiredTime">
              <Input
                inputClassName="text-[#2C2D33]"
                className="mt-5"
                placeholder="Thời gian hiệu lực"
              />
            </FormControl>
            <FormControl name="filename">
              <Input
                className="mt-3"
                inputClassName="!pl-4 pr-[50px]"
                placeholder="File"
                disabled
                inputIcon={Images.LinkIcon.default}
                inputIconClassName="!right-5 !top-[14px] !left-auto !w-6 !h-6 cursor-pointer hover:bg-gray-300"
                onIconClick={onAddFileVipClick}
              />
            </FormControl>
            <input
              ref={inputFileRefVip}
              type="file"
              name="file"
              hidden
              accept=""
              onChange={handleFileChangeVip}
            />
            <div className="mt-2">
              <Button
                type="submit"
                size="xs"
                label="Tải lên"
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
              />
            </div>
          </Form>
        </Formik>
      </div>
      <div className="bg-white rounded-lg p-4 w-96 ml-12">
        <Formik
          initialValues={{ bookId: 0, filename: "" }}
          onSubmit={onSubmitBook}
          innerRef={formRefBook}
          validateOnChange
          validateOnBlur
        >
          <Form>
            <div className="font-bold">Import mã sách</div>
            <FormControl name="bookId">
              <CustomSelect
                options={books}
                className="bg-color mt-5 !h-[52px] px-1 rounded-lg pr-4"
                optionContainerClassName="mt-4 rounded-b-lg w-full overflow-y-auto w-36"
                optionClassName="whitespace-nowrap px-3  border-b border-solid w-auto"
                labelSelectedClassName="black-50 !mx-3"
                placeholder="Chọn sách"
                arrow={<ChevronDownArrow />}
                hasBgColorHover
              />
            </FormControl>
            <FormControl name="filename">
              <Input
                className="mt-3"
                inputClassName="!pl-4 pr-[50px]"
                placeholder="File"
                disabled
                inputIcon={Images.LinkIcon.default}
                inputIconClassName="!right-5 !top-[14px] !left-auto !w-6 !h-6 cursor-pointer hover:bg-gray-300"
                onIconClick={onAddFileBookClick}
              />
            </FormControl>
            <input
              ref={inputFileRefBook}
              type="file"
              name="file"
              hidden
              accept=""
              onChange={handleFileChangeBook}
            />
            <div className="mt-2">
              <Button
                type="submit"
                size="xs"
                label="Tải lên"
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
              />
            </div>
          </Form>
        </Formik>
      </div>
      <div className="bg-white rounded-lg p-4 w-96 ml-12">
        <Formik
          initialValues={{ filename: "" }}
          onSubmit={onSubmitVoucherKas}
          innerRef={formRefVoucherKas}
          validateOnChange
          validateOnBlur
        >
          <Form>
            <div className="font-bold">Import mã tặng kas</div>
            <FormControl name="filename">
              <Input
                className="mt-3"
                inputClassName="!pl-4 pr-[50px]"
                placeholder="File"
                disabled
                inputIcon={Images.LinkIcon.default}
                inputIconClassName="!right-5 !top-[14px] !left-auto !w-6 !h-6 cursor-pointer hover:bg-gray-300"
                onIconClick={onAddFileVoucherKasClick}
              />
            </FormControl>
            <input
              ref={inputFileRefVoucherKas}
              type="file"
              name="file"
              hidden
              accept=""
              onChange={handleFileVoucherKas}
            />
            <div className="mt-2">
              <Button
                type="submit"
                size="xs"
                label="Tải lên"
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
              />
            </div>
          </Form>
        </Formik>
      </div>
    </div>
  );
}

export default VoucherImport;
